import { useMemo } from 'react';
import { useAppStore } from '../store';
import { getCategoryDescendants } from '../services/categoryService';
import type { Transaction } from '../types';

export interface UseTopTransactionsOptions {
  startDate: string;
  endDate: string;
  categoryIds?: string[];
  limit?: number;
}

export function useTopTransactions({
  startDate,
  endDate,
  categoryIds,
  limit = 10,
}: UseTopTransactionsOptions): Transaction[] {
  const transactions = useAppStore((state) => state.transactions);
  const categories = useAppStore((state) => state.categories);

  return useMemo(() => {
    let categoryIdSet: Set<string> | null = null;
    if (categoryIds && categoryIds.length > 0) {
      const allIds = new Set<string>();
      categoryIds.forEach((catId) => {
        allIds.add(catId);
        const descendants = getCategoryDescendants(categories, catId);
        descendants.forEach((d) => allIds.add(d.id));
      });
      categoryIdSet = allIds;
    }

    return transactions
      .filter((t) => {
        if (t.date < startDate || t.date > endDate) return false;
        if (t.type !== 'expense') return false;
        if (t.excludedFromBudget) return false;
        if (categoryIdSet) {
          if (!t.categoryId || !categoryIdSet.has(t.categoryId)) return false;
        }
        return true;
      })
      .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
      .slice(0, limit);
  }, [transactions, categories, startDate, endDate, categoryIds, limit]);
}
